import { ReactChild, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { DeleteOutlined } from '@ant-design/icons';
import {
    Button,
    Col,
    FormInstance,
    Row,
    Select,
} from 'antd';

import BaseForm from 'components/modules/BaseForm';
import { BaseSpin } from 'components/modules/Spin';
import * as Actions from 'flex/Actions';

import { Source } from '../../Common/UsageDataProvider';
import { createAppSelectOptions } from '../../Common/utils';
import useSelectScroll from './hooks/useSelectScroll';
import SelectScroll from './SelectScroll';

const Form = BaseForm;
const { Option } = Select;
const PAGE_SIZE = 50;

const CenterSpin = () => (
    <div style={{ textAlign: 'center' }}>
        <BaseSpin size='small' />
    </div>
);

type Props = {
    name: string;
    form: FormInstance;
    organizationList: Source['organizationList'];
    organizationAppLists: Source['organizationAppLists'];
    onRemove: () => void;
};

const INITIAL_APP_LIST_STATE = {
    list: [],
    loading: false,
    pageTotal: 1,
    page: 1,
}

const AppSelectWithGroup: React.FC<Props> = ({
    name,
    form,
    organizationList,
    organizationAppLists,
    onRemove,
}) => {
    const organizationUuid = form.getFieldsValue().targetDetails[name].group;
    const organizationAppInfo = organizationAppLists[organizationUuid] || INITIAL_APP_LIST_STATE;
    // セレクトボックスの開閉時に連続したAPI呼び出しを防ぐためのフラグ。
    const [isInput, setIsInput] = useState(false);

    const dispatch = useDispatch();

    const { dataOptions, isScrolling, isSearching, hasMore, handleNextPage, handleSearch } = useSelectScroll({
        queryKey: 'title',
        query: [{ 'organization_uuid': organizationUuid }],
        data: organizationAppInfo,
        selector: (state: Store) => state.appsListConnection,
        fetchAction: Actions.http.connection.apps.search,
    });

    useEffect(() => {
        if (organizationUuid === undefined || organizationAppInfo.list.length > 0) return;
        dispatch(Actions.http.connection.apps.search({ page_size: PAGE_SIZE, organization_uuid: organizationUuid}));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationUuid]);

    useEffect(() => {
        if (organizationAppInfo.list.length === 0) return;
        const targetDetails = form.getFieldsValue().targetDetails;
        targetDetails[name] = { app: 'all', group: organizationUuid };
        form.setFieldsValue({ targetDetails });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationAppInfo.list.length]);

    const onChangeAppGroup = (value: unknown) => {
        const fields = form.getFieldsValue();
        const { targetDetails } = fields;
        Object.assign(targetDetails[name], { group: value });
        form.setFieldsValue({ targetDetails });
        form.resetFields([['targetDetails', name, 'app']]);
    };

    const onChangeApp = (value: unknown) => {
        const fields = form.getFieldsValue();
        const { targetDetails } = fields;
        Object.assign(targetDetails[name], { app: value ?? 'all' });
        form.setFieldsValue({ targetDetails });
    };

    return (
        <div className='search-target-detail'>
            <Row gutter={8}>
                <Col span={12}>
                    <Form.Item
                        name={[name, 'group']}
                    >
                        <Select
                            allowClear
                            filterOption={(input, option) =>{
                                // 型がおかしいので、それらしい型に変換している
                                const children = option?.children as unknown as ReactChild;

                                if (typeof children === 'string') {
                                    return children.toLowerCase().includes(input.toLowerCase());
                                } else {
                                    // ここには来ないはず
                                    return false;
                                }
                            }}
                            notFoundContent={
                                organizationList.length === 0 ?
                                    <CenterSpin /> :
                                    undefined
                            }
                            onChange={onChangeAppGroup}
                            placeholder='対象を選択してください'
                            showSearch
                        >
                            {organizationList.map(org => {
                                return <Option key={org.value} value={org.value}>{org.text}</Option>;
                            })}
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item name={[name, 'app']}>
                        <SelectScroll
                            hasMore={hasMore}
                            isSearching={isSearching}
                            next={handleNextPage}
                            options={dataOptions}
                            search={handleSearch}
                            renderItem={({
                                options,
                                searchValue,
                                loadMore,
                                notFoundContent,
                                handlePopupScroll,
                                handleSearch: onSearch,
                            }) => {
                                return (
                                    <Select
                                        allowClear
                                        filterOption={false}
                                        notFoundContent={notFoundContent}
                                        onChange={onChangeApp}
                                        onPopupScroll={handlePopupScroll}
                                        onSearch={!isScrolling ? onSearch : undefined}
                                        onDropdownVisibleChange={(open) => {
                                            if (!open && isInput) onSearch('');
                                            // フラグをリセットする。
                                            setIsInput(false);
                                        }}
                                        onInputKeyDown={(event) =>
                                            event.ctrlKey ||
                                            event.altKey ||
                                            event.shiftKey
                                                ? setIsInput(false)
                                                : setIsInput(true)
                                        }
                                        placeholder='対象を選択してください'
                                        searchValue={searchValue}
                                        showSearch> 
                                        {createAppSelectOptions(options, searchValue)}

                                        {loadMore}
                                    </Select>
                                );
                            }}
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Button
                className='mr-2'
                danger
                icon={<DeleteOutlined />}
                onClick={onRemove}
                shape='circle'
                size='small'
            />
        </div>
    );
};

export default AppSelectWithGroup;
